export const ITEM_SOURCE = {
  STATIC_TEMPLATE_ITEMS: "STATIC_TEMPLATE_ITEMS",
  EMPLOYEE_GOALS: "EMPLOYEE_GOALS",
  COMPETENCY_MASTER: "COMPETENCY_MASTER",
  APPRAISAL_360: "APPRAISAL_360",
  MANAGER_ASSESSMENT: "MANAGER_ASSESSMENT",
};

export const SOURCE_TO_BACKEND = {
  [ITEM_SOURCE.STATIC_TEMPLATE_ITEMS]: "MANUAL",
  [ITEM_SOURCE.EMPLOYEE_GOALS]: "EMPLOYEE_GOALS",
  [ITEM_SOURCE.COMPETENCY_MASTER]: "COMPETENCY_MASTER",
  [ITEM_SOURCE.APPRAISAL_360]: "360_CAMPAIGN",
  [ITEM_SOURCE.MANAGER_ASSESSMENT]: "MANUAL",
};

export const BACKEND_TO_SOURCE = {
  MANUAL: ITEM_SOURCE.STATIC_TEMPLATE_ITEMS,
  CUSTOM: ITEM_SOURCE.STATIC_TEMPLATE_ITEMS,
  CUSTOM_ITEMS: ITEM_SOURCE.STATIC_TEMPLATE_ITEMS,
  EMPLOYEE_GOALS: ITEM_SOURCE.EMPLOYEE_GOALS,
  COMPETENCY_MASTER: ITEM_SOURCE.COMPETENCY_MASTER,
  "360_CAMPAIGN": ITEM_SOURCE.APPRAISAL_360,
  APPRAISAL_360: ITEM_SOURCE.APPRAISAL_360,
  MANAGER_ASSESSMENT: ITEM_SOURCE.MANAGER_ASSESSMENT,
};

export const sourceLabels = {
  [ITEM_SOURCE.STATIC_TEMPLATE_ITEMS]: "Template items",
  [ITEM_SOURCE.EMPLOYEE_GOALS]: "Employee goals",
  [ITEM_SOURCE.COMPETENCY_MASTER]: "Competency master",
  [ITEM_SOURCE.APPRAISAL_360]: "360 appraisal",
  [ITEM_SOURCE.MANAGER_ASSESSMENT]: "Manager assessment",
};

export const statusLabels = {
  READY: "Ready",
  INCOMPLETE: "Incomplete",
  DYNAMIC: "Dynamic",
  WARNING: "Warning",
  ERROR: "Error",
  NOT_REQUIRED: "Not required",
  PENDING_SAVE: "Pending save",
};

export const statusColorSchemes = {
  READY: "green",
  INCOMPLETE: "orange",
  DYNAMIC: "blue",
  WARNING: "yellow",
  ERROR: "red",
  NOT_REQUIRED: "gray",
  PENDING_SAVE: "purple",
};

const newId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `item_${Date.now()}_${Math.random().toString(36).slice(2)}`;
};

export const createTemplateItem = (overrides = {}) => ({
  itemId: newId(),
  itemCode: "",
  sequence: 0,
  title: "",
  description: "",
  kpiMeasure: "",
  itemType: "TEMPLATE_ITEM",
  sourceType: ITEM_SOURCE.STATIC_TEMPLATE_ITEMS,
  weightage: 0,
  ratingRequired: true,
  commentRequired: false,
  evidenceRequired: false,
  allowEmployeeInput: false,
  allowManagerEdit: true,
  active: true,
  ...overrides,
});

export const cloneTemplateItem = (item = {}, overrides = {}) => {
  const {
    fieldKey,
    id,
    _id,
    itemId,
    ...businessFields
  } = item;

  return createTemplateItem({
    ...businessFields,
    itemCode: businessFields.itemCode ? `${businessFields.itemCode}_COPY` : "",
    ...overrides,
  });
};

export const criteriaItem = (title, description, sequence) =>
  createTemplateItem({
    itemCode: title.toUpperCase().replace(/[^A-Z0-9]+/g, "_").replace(/^_|_$/g, ""),
    sequence,
    title,
    description,
    weightage: 0,
    ratingRequired: true,
    commentRequired: false,
    evidenceRequired: false,
    allowEmployeeInput: false,
    allowManagerEdit: true,
    active: true,
  });

export const createExcelDefaultSections = () => [
  {
    sectionId: "KRA",
    sequence: 1,
    title: "Key Result Areas",
    code: "KRA",
    description: "Role-specific result areas and KPI measures.",
    type: "GOALS",
    sectionType: "GOALS",
    weightage: 40,
    calculationMethod: "WEIGHTED_ITEMS",
    itemSource: SOURCE_TO_BACKEND[ITEM_SOURCE.STATIC_TEMPLATE_ITEMS],
    uiItemSource: ITEM_SOURCE.STATIC_TEMPLATE_ITEMS,
    isRequired: true,
    mandatory: true,
    allowEmployeeRating: true,
    allowManagerRating: true,
    allowComments: true,
    commentRequired: false,
    evidenceRequired: false,
    minimumItemsRequired: 1,
    items: [
      createTemplateItem({ itemCode: "KRA_1", sequence: 1, weightage: 15 }),
      createTemplateItem({ itemCode: "KRA_2", sequence: 2, weightage: 10 }),
      createTemplateItem({ itemCode: "KRA_3", sequence: 3, weightage: 5 }),
      createTemplateItem({ itemCode: "KRA_4", sequence: 4, weightage: 5 }),
      createTemplateItem({ itemCode: "KRA_5", sequence: 5, weightage: 5 }),
    ],
  },
  {
    sectionId: "BEHAVIOUR_VALUES",
    sequence: 2,
    title: "Behaviour & Values",
    code: "BEHAVIOUR_VALUES",
    description: "Rate employees based on observable behaviours.",
    type: "BEHAVIOUR",
    sectionType: "BEHAVIOUR",
    weightage: 25,
    calculationMethod: "AVERAGE_RATING",
    itemSource: SOURCE_TO_BACKEND[ITEM_SOURCE.STATIC_TEMPLATE_ITEMS],
    uiItemSource: ITEM_SOURCE.STATIC_TEMPLATE_ITEMS,
    isRequired: true,
    mandatory: true,
    allowEmployeeRating: true,
    allowManagerRating: true,
    allowComments: true,
    commentRequired: false,
    evidenceRequired: false,
    minimumItemsRequired: 1,
    items: [
      criteriaItem(
        "Ownership",
        "Takes responsibility for assigned work, proactively identifies and resolves issues, follows through on commitments, and completes tasks without frequent reminders.",
        1,
      ),
      criteriaItem(
        "Accountability",
        "Accepts responsibility for outcomes, meets agreed deadlines, maintains quality of work, and takes corrective action when expectations are not met.",
        2,
      ),
      criteriaItem(
        "Communication",
        "Communicates information clearly, accurately, and in a timely manner; keeps relevant stakeholders informed of progress and issues; shares information transparently; responds promptly to queries; and escalates concerns to the appropriate authority when required.",
        3,
      ),
      criteriaItem(
        "Teamwork",
        "Works effectively with colleagues across teams, shares knowledge, supports team objectives, respects diverse viewpoints, and contributes positively to team success.",
        4,
      ),
      criteriaItem(
        "Professional Conduct",
        "Demonstrates professionalism in behaviour, maintains workplace discipline, treats colleagues, customers, and stakeholders with courtesy and respect, and upholds company values.",
        5,
      ),
      criteriaItem(
        "Integrity & Ethics",
        "Acts honestly and transparently, maintains confidentiality, complies with company policies, uses company resources responsibly, and demonstrates ethical decision-making.",
        6,
      ),
      criteriaItem(
        "Emotional Maturity",
        "Remains calm under pressure, accepts constructive feedback positively, manages emotions appropriately, handles conflicts professionally, and demonstrates resilience during change or challenging situations.",
        7,
      ),
    ],
  },
  {
    sectionId: "DISCIPLINE_PROCESS",
    sequence: 3,
    title: "Discipline & Process Excellence",
    code: "DISCIPLINE_PROCESS",
    description: "Discipline, process adherence and reliable execution.",
    type: "DISCIPLINE",
    sectionType: "DISCIPLINE",
    weightage: 20,
    calculationMethod: "AVERAGE_RATING",
    itemSource: SOURCE_TO_BACKEND[ITEM_SOURCE.STATIC_TEMPLATE_ITEMS],
    uiItemSource: ITEM_SOURCE.STATIC_TEMPLATE_ITEMS,
    isRequired: true,
    mandatory: true,
    allowEmployeeRating: true,
    allowManagerRating: true,
    allowComments: true,
    commentRequired: false,
    evidenceRequired: false,
    minimumItemsRequired: 1,
    items: [
      criteriaItem(
        "Attendance & Punctuality",
        "Maintains regular attendance, reports to work on time, adheres to approved working hours, plans leave responsibly, and minimizes unplanned absenteeism or late arrivals.",
        1,
      ),
      criteriaItem(
        "SOP Compliance",
        "Consistently follows established Standard Operating Procedures (SOPs), company policies, safety guidelines, and work instructions; completes assigned activities in accordance with defined processes and statutory requirements.",
        2,
      ),
      criteriaItem(
        "Documentation",
        "Maintains accurate, complete, and up-to-date records and documentation; submits reports within agreed timelines; and ensures information is organized, traceable, and readily available for operational requirements, audits, and management review.",
        3,
      ),
      criteriaItem(
        "Responsiveness",
        "Responds promptly to requests, emails, and action items; provides timely updates on assigned tasks; escalates issues appropriately; and ensures commitments are closed within agreed timelines.",
        4,
      ),
      criteriaItem(
        "Reliability",
        "Demonstrates reliability by consistently delivering assigned responsibilities, honouring commitments, meeting deadlines, and following through on tasks with minimal supervision.",
        5,
      ),
    ],
  },
  {
    sectionId: "GROWTH_IMPROVEMENT",
    sequence: 4,
    title: "Growth & Continuous Improvement",
    code: "GROWTH_IMPROVEMENT",
    description: "Learning, improvement mindset and adoption of better ways of working.",
    type: "GROWTH",
    sectionType: "GROWTH",
    weightage: 15,
    calculationMethod: "AVERAGE_RATING",
    itemSource: SOURCE_TO_BACKEND[ITEM_SOURCE.STATIC_TEMPLATE_ITEMS],
    uiItemSource: ITEM_SOURCE.STATIC_TEMPLATE_ITEMS,
    isRequired: true,
    mandatory: true,
    allowEmployeeRating: true,
    allowManagerRating: true,
    allowComments: true,
    commentRequired: false,
    evidenceRequired: false,
    minimumItemsRequired: 1,
    items: [
      criteriaItem(
        "Adaptability",
        "Demonstrates openness to change, adjusts effectively to new responsibilities, processes, technologies, and business requirements, and maintains a positive approach during transitions.",
        1,
      ),
      criteriaItem(
        "Learning Agility",
        "Actively pursues learning opportunities, seeks and applies feedback, acquires new knowledge and skills, and demonstrates a commitment to continuous self-development.",
        2,
      ),
      criteriaItem(
        "Continuous Improvement",
        "Identifies opportunities to improve work processes, enhances efficiency, quality, or productivity, recommends practical solutions, and actively contributes to implementing improvements within the department or organization.",
        3,
      ),
      criteriaItem(
        "Knowledge Sharing",
        "Shares knowledge, best practices, and experiences with colleagues, supports team learning, and willingly mentors or guides others whenever required.",
        4,
      ),
      criteriaItem(
        "System Adoption",
        "Effectively utilizes company systems, digital tools, and technology, quickly adapts to new applications or process changes, and promotes efficient use of organizational systems.",
        5,
      ),
    ],
  },
];

const DRIVER_SECTION_ITEMS = {
  BEHAVIOUR_VALUES: [
    ["Ownership", "Takes responsibility for the assigned vehicle, trips, passengers or goods, and duties without repeated follow-up."],
    ["Accountability", "Accepts responsibility for driving conduct, delays, vehicle condition, incidents, and assigned responsibilities."],
    ["Communication", "Keeps supervisors, passengers, and stakeholders informed about arrival, delays, route changes, breakdowns, and other issues."],
    ["Teamwork", "Cooperates with staff, security, logistics, administration, and other drivers to support operational requirements."],
    ["Professional Conduct", "Maintains courteous behaviour, appropriate appearance, respectful communication, and professional conduct while representing the company."],
    ["Integrity & Ethics", "Uses company vehicles, fuel, FASTag, cash, and other resources honestly and only for authorized purposes."],
    ["Emotional Maturity", "Remains calm in traffic, avoids aggressive driving and road rage, handles passenger concerns professionally, and responds appropriately under pressure."],
  ],
  DISCIPLINE_PROCESS: [
    ["Attendance & Punctuality", "Reports for duty on time, maintains regular attendance, and communicates leave or unavoidable delays promptly."],
    ["SOP & Safety Compliance", "Follows traffic laws, company driving policies, seat-belt rules, parking procedures, vehicle-use policies, and safety requirements."],
    ["Trip Documentation", "Maintains accurate trip sheets, kilometre readings, fuel records, toll and expense receipts, and required vehicle records."],
    ["Responsiveness", "Responds promptly to trip assignments, calls, and schedule changes, and reports breakdowns, accidents, or major delays immediately."],
    ["Reliability", "Remains available for assigned duties, completes trips as committed, and can be trusted with the vehicle and assigned responsibilities."],
  ],
  GROWTH_IMPROVEMENT: [
    ["Adaptability", "Adjusts to changing routes, schedules, vehicles, passengers, and operational requirements."],
    ["Learning Agility", "Learns updated traffic rules, safety practices, vehicle features, and company procedures."],
    ["Continuous Improvement", "Improves driving safety, route planning, fuel efficiency, vehicle care, and overall service quality."],
    ["Knowledge Sharing", "Shares route knowledge, vehicle issues, safety observations, and useful operational information with colleagues."],
    ["System Adoption", "Correctly uses GPS and navigation, attendance systems, trip applications, digital expense tools, and other company systems."],
  ],
};

export const createDriverTemplatePreset = () => {
  const sections = createExcelDefaultSections();
  const splitWeightage = (total, count) => {
    const base = Math.floor((total / count) * 10000) / 10000;
    const finalWeight = Number((total - base * (count - 1)).toFixed(4));
    return [...Array(Math.max(count - 1, 0)).fill(base), finalWeight];
  };
  const kraItems = [
    ["Safe Driving & Accident Prevention", "Follows traffic and company safety rules; avoids preventable accidents, unsafe driving, overspeeding, and traffic violations.", 15],
    ["Timely Trips & Route Management", "Reports on time, completes assigned trips within expected timelines, and selects efficient routes.", 10],
    ["Vehicle Care & Maintenance", "Performs routine vehicle checks, reports faults promptly, and maintains vehicle condition.", 5],
    ["Fuel Efficiency & Cost Control", "Avoids unnecessary idling, follows efficient driving practices, and controls fuel and operating expenses.", 5],
    ["Trip & Duty Completion", "Completes assigned pickups, drops, deliveries, and other driving duties accurately and reliably.", 5],
  ];

  return {
    name: "Driver Performance Appraisal",
    code: "DRIVER_PERFORMANCE_APPRAISAL",
    description: "Performance appraisal template for drivers covering safe and reliable driving, trip execution, vehicle care, professional conduct, process discipline, and continuous improvement.",
    totalWeightage: 100,
    sectionWeightageMode: "STRICT_100",
    itemWeightageMode: "ABSOLUTE_SECTION_WEIGHT",
    workflowConfiguration: { selfAssessmentRequired: false },
    developmentPlanConfiguration: {
      required: true,
      fields: ["developmentArea", "actionPlan", "supportRequired", "timeline"],
    },
    sections: sections.map((section) => {
      if (section.code === "KRA") {
        return {
          ...section,
          items: kraItems.map(([title, description, weightage], index) =>
            createTemplateItem({
              itemCode: `KRA_${index + 1}`,
              sequence: index + 1,
              title,
              description,
              weightage,
              kpiMeasure: description,
            }),
          ),
        };
      }

      const items = DRIVER_SECTION_ITEMS[section.code];
      if (!items) return section;

      const itemWeights = splitWeightage(section.weightage, items.length);
      return {
        ...section,
        items: items.map(([title, description], index) => ({
          ...criteriaItem(title, description, index + 1),
          weightage: itemWeights[index],
        })),
      };
    }),
  };
};

export const toUiSource = (section = {}) =>
  section.uiItemSource ||
  BACKEND_TO_SOURCE[section.itemSource] ||
  (section.calculationMethod === "GOAL_PROGRESS_WEIGHTED"
    ? ITEM_SOURCE.EMPLOYEE_GOALS
    : ITEM_SOURCE.STATIC_TEMPLATE_ITEMS);

export const toBackendItemSource = (uiSource) =>
  SOURCE_TO_BACKEND[uiSource] || uiSource || "MANUAL";

export const isStaticSource = (section) =>
  toUiSource(section) === ITEM_SOURCE.STATIC_TEMPLATE_ITEMS;

export const getSectionStatus = (section = {}, isDirty = false) => {
  const uiSource = toUiSource(section);
  const items = (section.items || []).filter((item) => item.active !== false);
  if (isDirty) return "PENDING_SAVE";
  if (uiSource === ITEM_SOURCE.EMPLOYEE_GOALS || uiSource === ITEM_SOURCE.APPRAISAL_360) {
    return "DYNAMIC";
  }
  if (uiSource === ITEM_SOURCE.MANAGER_ASSESSMENT) return "NOT_REQUIRED";
  if (Number(section.minimumItemsRequired || 0) > 0 && items.length === 0) {
    return "INCOMPLETE";
  }
  return items.length ? "READY" : "INCOMPLETE";
};

export const getSectionValidation = (section = {}, itemWeightageMode = "RELATIVE_PERCENTAGE") => {
  const messages = [];
  const activeItems = (section.items || []).filter((item) => item.active !== false);
  const itemTotal = activeItems.reduce((sum, item) => sum + Number(item.weightage || 0), 0);
  const minimumItemsRequired = Number(section.minimumItemsRequired || 0);

  if (isStaticSource(section) && minimumItemsRequired > 0 && activeItems.length < minimumItemsRequired) {
    messages.push({ tone: "orange", message: "At least one row is required." });
  }
  if (section.code === "KRA" && itemWeightageMode === "ABSOLUTE_SECTION_WEIGHT") {
    if (itemTotal < Number(section.weightage || 0)) {
      messages.push({ tone: "orange", message: `Configured weightage is ${itemTotal}. Required weightage is ${section.weightage}.` });
    }
    if (itemTotal > Number(section.weightage || 0)) {
      messages.push({ tone: "red", message: `Configured weightage is ${itemTotal}. Required weightage is ${section.weightage}.` });
    }
  }
  activeItems.forEach((item, index) => {
    if (!String(item.title || "").trim()) {
      messages.push({ tone: "orange", message: `Row ${index + 1} is missing a title.` });
    }
    if (section.code !== "KRA" && !String(item.description || "").trim()) {
      messages.push({ tone: "orange", message: `Row ${index + 1} is missing a behaviour indicator.` });
    }
  });

  return { itemTotal, messages };
};
